import type { AgentTemplate, TemplateCategory } from "../types";
import { computed, ref } from "vue";
import { $t } from "@/services/i18n";
import { CATEGORY_ORDER, TEMPLATE_CATEGORIES } from "../categories";
import { useTemplates } from "../index";

/**
 * Hook para gerenciar estado do seletor de templates
 */
export function useTemplateSelector() {
  const { templates, getTemplateById } = useTemplates();

  const selectedCategory = ref<TemplateCategory | "all">("all");
  const searchQuery = ref("");
  const selectedTemplateId = ref<string | null>(null);

  /**
   * Templates filtrados por categoria e busca
   */
  const filteredTemplates = computed(() => {
    let result = templates.value;

    // Filtrar por categoria
    if (selectedCategory.value !== "all") {
      result = result.filter((t) => t.category === selectedCategory.value);
    }

    // Filtrar por busca (tags, id, nome traduzido)
    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase();
      result = result.filter((t) => {
        const nameMatch = $t(t.nameKey as any).toLowerCase().includes(query);
        const descMatch = $t(t.descriptionKey as any).toLowerCase().includes(query);
        const tagMatch = t.tags.some((tag) => tag.includes(query));
        const idMatch = t.id.toLowerCase().includes(query);
        return nameMatch || descMatch || tagMatch || idMatch;
      });
    }

    return result;
  });

  /**
   * Template selecionado para preview
   */
  const selectedTemplate = computed<AgentTemplate | null>(() =>
    selectedTemplateId.value ? getTemplateById(selectedTemplateId.value).value ?? null : null
  );

  /**
   * Lista de categorias com metadados
   */
  const categories = computed(() => CATEGORY_ORDER.map((id) => TEMPLATE_CATEGORIES[id]));

  /**
   * Contagem de templates por categoria
   */
  const templateCountByCategory = computed(() => {
    const counts: Record<TemplateCategory | "all", number> = {
      all: templates.value.length,
      "customer-service": 0,
      sales: 0,
      internal: 0,
      healthcare: 0,
      financial: 0,
      utilities: 0,
    };

    for (const template of templates.value) {
      counts[template.category]++;
    }

    return counts;
  });

  /**
   * Seleciona um template para preview
   */
  function selectTemplate(id: string | null) {
    selectedTemplateId.value = id;
  }

  /**
   * Seleciona uma categoria
   */
  function selectCategory(category: TemplateCategory | "all") {
    selectedCategory.value = category;
    selectedTemplateId.value = null;
  }

  /**
   * Limpa a seleção
   */
  function clearSelection() {
    selectedTemplateId.value = null;
    searchQuery.value = "";
  }

  return {
    // Estado
    templates: filteredTemplates,
    categories,
    selectedCategory,
    searchQuery,
    selectedTemplate,
    selectedTemplateId,
    templateCountByCategory,

    // Ações
    selectTemplate,
    selectCategory,
    clearSelection,
  };
}
